/* eslint-disable sorare/no-detached-fragments */
import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { isType } from '@sorare/core/src/lib/gql';

import type {
  TaskClaimedDialogs_anyRewardInterface,
  TaskClaimedDialogs_taskInterface,
} from './__generated__/index.graphql.ts';
import type {
  ClaimTaskWithPackMutation,
  ClaimTaskWithPackMutationVariables,
} from './__generated__/useTaskClaimedDialogs.graphql.ts';
import { TaskClaimedDialogs } from './index.tsx';

const CLAIM_TASK_WITH_PACK_MUTATION = gql`
  mutation ClaimTaskWithPackMutation($taskId: ID!) {
    claimTask(input: { taskId: $taskId }) {
      task {
        id
        aasmState
      }
      rewards {
        id
        ...TaskClaimedDialogs_anyRewardInterface
      }

      errors {
        message
        code
      }
    }
  }
  ${TaskClaimedDialogs.fragments.anyRewardInterface}
` as TypedDocumentNode<
  ClaimTaskWithPackMutation,
  ClaimTaskWithPackMutationVariables
>;

export const useTaskClaimedDialogs = () => {
  const [claimedTask, setClaimedTask] =
    useState<TaskClaimedDialogs_taskInterface | null>(null);
  const [rewardDialogTask, setRewardDialogTask] =
    useState<TaskClaimedDialogs_taskInterface | null>(null);
  const [revealPackCardsDialogReward, setRevealPackCardsDialogReward] =
    useState<TaskClaimedDialogs_anyRewardInterface | null>(null);
  const [pickerDialogTask, setPickerDialogTask] =
    useState<TaskClaimedDialogs_taskInterface | null>(null);

  const [claimTaskWithPack, { loading: loadingClaimTaskWithPack }] =
    useMutation(CLAIM_TASK_WITH_PACK_MUTATION, {
      showErrorsWithSnackNotification: true,
    });

  const onClaim = async (task: TaskClaimedDialogs_taskInterface) => {
    setClaimedTask(task);

    const firstRewardConfig = task.rewardConfigs.at(0);
    const hasCardPackReward = isType(
      firstRewardConfig,
      'CardPacksRewardConfig'
    );

    if (hasCardPackReward) {
      const { data: dataClaimTask } = await claimTaskWithPack({
        variables: {
          taskId: task.id,
        },
      });
      const reward = dataClaimTask?.claimTask?.rewards?.at(0);
      if (reward) {
        setRevealPackCardsDialogReward(reward);
      }
    } else {
      setRewardDialogTask(task);
    }
  };

  return {
    onClaim,
    loadingTaskId: loadingClaimTaskWithPack && claimedTask?.id,
    rewardDialogTask,
    setRewardDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    pickerDialogTask,
    setPickerDialogTask,
  };
};
